/**
 * Friendly handle helpers for people who have not picked a display name yet.
 *
 * Suggestions lean on the device the person is chatting from ("iPhone 15 Pro",
 * "MacBook Pro 14 M1 Pro", "Pixel 8") and a stable hash of the per-browser id
 * keeps them unique. Browsers rarely expose exact hardware, so labels are
 * best-effort and always meant to be edited by the person.
 */
export interface DeviceHints {
  userAgent?: string;
  /** CSS-pixel screen size; used to name iOS and Mac hardware, which hide their model. */
  screen?: [number, number];
  /** User-Agent Client Hints model when the browser offers it ("Pixel 8 Pro"). */
  model?: string;
  /**
   * Unmasked WebGL renderer. On Apple Silicon this is the only place a browser
   * leaks the chip, e.g. "ANGLE Metal Renderer: Apple M1 Pro".
   */
  gpu?: string;
  /** Device pixel ratio, so Retina Mac panels are not confused with external monitors. */
  pixelRatio?: number;
}

function hashId(id: string): number {
  let hash = 2166136261;
  for (let index = 0; index < id.length; index += 1) {
    hash ^= id.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function suffix(id: string): string {
  return String(hashId(id) % 10000).padStart(4, '0');
}

/** Deterministic handle used when nothing about the device is known. */
export function generatedName(id: string): string {
  return `Chatsi#${suffix(id)}`;
}

/** iPhone/iPad models keyed by their CSS-pixel screen size (either orientation). */
const IOS_SCREENS: Array<[number, number, string]> = [
  [320, 568, 'iPhone SE'],
  [375, 667, 'iPhone 8'],
  [375, 812, 'iPhone 13 mini'],
  [390, 844, 'iPhone 14'],
  [393, 852, 'iPhone 15 Pro'],
  [402, 874, 'iPhone 16 Pro'],
  [414, 896, 'iPhone 11'],
  [428, 926, 'iPhone 14 Plus'],
  [430, 932, 'iPhone 15 Pro Max'],
  [440, 956, 'iPhone 16 Pro Max'],
  [744, 1133, 'iPad mini'],
  [768, 1024, 'iPad'],
  [810, 1080, 'iPad 10.2'],
  [820, 1180, 'iPad Air'],
  [834, 1112, 'iPad Air 10.5'],
  [834, 1194, 'iPad Pro 11'],
  [1024, 1366, 'iPad Pro 12.9'],
];

function iosModel(screen?: [number, number]): string {
  if (!screen) return '';
  const short = Math.min(screen[0], screen[1]);
  const long = Math.max(screen[0], screen[1]);
  const match = IOS_SCREENS.find(([width, height]) => width === short && height === long);
  return match ? match[2] : '';
}

/** Default "looks like" sizes of Retina Mac panels, short side first. */
const MAC_SCREENS: Array<[number, number, string]> = [
  [982, 1512, 'MacBook Pro 14'],
  [1117, 1728, 'MacBook Pro 16'],
  [956, 1470, 'MacBook Air 13'],
  [1112, 1710, 'MacBook Air 15'],
  [900, 1440, 'MacBook'],
  [1260, 2240, 'iMac 24'],
];

/** "ANGLE Metal Renderer: Apple M1 Pro" -> "M1 Pro"; Safari's "Apple GPU" -> "". */
function appleChip(gpu?: string): string {
  const match = /apple\s+m(\d{1,2})(?:\s+(pro|max|ultra))?\b/i.exec(gpu ?? '');
  if (!match) return '';
  const tier = match[2] ? ` ${match[2][0].toUpperCase()}${match[2].slice(1).toLowerCase()}` : '';
  return `M${match[1]}${tier}`;
}

function macFamily(hints: DeviceHints): string {
  const { screen, pixelRatio } = hints;
  // Retina panels report a fractional ratio once the page is zoomed, so allow 1.5+;
  // a plain external monitor still reports 1 and is never matched.
  if (!screen || (pixelRatio ?? 1) < 1.5) return '';
  const short = Math.min(screen[0], screen[1]);
  const long = Math.max(screen[0], screen[1]);
  const match = MAC_SCREENS.find(([width, height]) => width === short && height === long);
  return match ? match[2] : '';
}

/** "MacBook Pro 14 M1 Pro", degrading to "Mac M1 Pro" or plain "Mac". */
function macLabel(hints: DeviceHints): string {
  const chip = appleChip(hints.gpu);
  const family = macFamily(hints);
  if (family && chip) return `${family} ${chip}`;
  if (family) return family;
  if (chip) return `Mac ${chip}`;
  return 'Mac';
}

/** Best-effort human label for the current device; empty when unrecognised. */
export function deviceLabel(hints: DeviceHints = {}): string {
  const model = (hints.model ?? '').replace(/\s+/g, ' ').trim();
  if (model) return model.slice(0, 32);

  const ua = hints.userAgent ?? '';
  if (/iPhone/i.test(ua)) return iosModel(hints.screen) || 'iPhone';
  if (/iPad/i.test(ua)) return iosModel(hints.screen) || 'iPad';
  if (/iPod/i.test(ua)) return 'iPod touch';
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? 'Android phone' : 'Android tablet';
  if (/CrOS/i.test(ua)) return 'Chromebook';
  if (/Macintosh|Mac OS X/i.test(ua)) return macLabel(hints);
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Linux/i.test(ua)) return 'Linux';
  return '';
}

/**
 * Name suggested until someone picks their own, e.g. "iPhone 15 Pro #4271".
 * Falls back to the plain generated handle when the device is unknown.
 */
export function suggestedName(id: string, hints: DeviceHints = {}): string {
  const label = deviceLabel(hints);
  const name = label ? `${label} #${suffix(id)}` : generatedName(id);
  return name.slice(0, 48);
}

/** True for handles this module generates, so unnamed people can be re-suggested. */
export function isGeneratedName(name: string): boolean {
  const value = name.trim();
  if (!value) return false;
  return /^Chatsi#\d{4}$/.test(value) || /^(.*\S) #\d{4}$/.test(value);
}
